import { useFlag } from '@calendly/feature-check-react';
import { I18n } from '@calendly/i18n';
import { useDeviceType } from '@calendly/ui/hooks/use-device-type';
import classNames from 'classnames';
import { forwardRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { dismissExportConfirmation } from 'actions/scheduled_events/export';
import { useGetUserQuery } from 'common';
import ExportConfirmation from 'components/export/confirmation';
import ScopeDropdown from 'components/home_bar/scope_dropdown';
import { MeetingsFilter } from 'components/meetings';
import { useProductRefreshAppShell } from 'features/app_shell/product_refresh/useProductRefreshAppShell';
import { PageFilterRenderer } from 'features/app_shell/shell_layout/pageFilterContext';
import { useGetMeetingIntelligenceUserSettingsQuery, useNotetakerEntitled, StepTwo, MI_MEETINGS_TOUR, handleCheckStepEnabled } from 'features/meeting_intelligence';
import { useFilters } from 'hooks/meetings/filters';
import { isExportConfirmationVisibleSelector } from 'selectors/scheduled_events';
import { ExportButton } from './export_button';
import { useGetMeetingsFilterCountQuery, useSearchMeetingsSupergraphFilterCountQuery } from './graphql_api';
import { createUseResultsCount } from './hooks/use_results_count';
import { Search as SearchInput } from './meetings_page_search_input';
import { MeetingsPresetTabs } from './meetings_preset_tabs';
import { MeetingsSchedulingSourceSwitch } from './meetings_scheduling_source_switch';
import { PersistentFiltersMenu } from './persistent_filters_menu';
const headerContainerIsPhoneCss = "h1pzthai";
const headerContainerMispotlightCss = "h1ucv90c";
const headerContainerIsPretendingCss = "h2y0gce";
const headerContainerCss = "h49spf8";
const headerContainerLegacyCss = "htz5rl1";

/**
 * Legacy shell + phone preset: the utility row already paints the hairline. The legacy
 * container's padding-bottom (16) + margin-bottom (24) was stacking under that line into
 * ~40px before "Displaying N" (measured 41px; target 2px on MeetingsPhoneDisplayedCount).
 */
const headerContainerLegacyPhonePresetFlushCss = "h1mv259i";
const HeaderContainer = forwardRef((props, ref) => {
  const {
    mispotlight,
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  const isPhone = useDeviceType('isPhone');
  const {
    data
  } = useGetUserQuery();
  const isPretending = data?.is_pretending;
  const isProductRefreshAppShell = useProductRefreshAppShell();
  const isDefaultPresetToggleEnabled = useFlag('sa_default_preset_toggle', false);
  const flushLegacyPhonePreset = !isProductRefreshAppShell && isPhone && isDefaultPresetToggleEnabled;
  return <Component ref={ref} {...restProps} className={classNames(headerContainerCss, {
    // legacy shell only; new shell hoists to top chrome via PageFilterRenderer
    [headerContainerLegacyCss]: !isProductRefreshAppShell,
    [headerContainerLegacyPhonePresetFlushCss]: flushLegacyPhonePreset,
    [headerContainerIsPhoneCss]: isPhone,
    [headerContainerIsPretendingCss]: isPretending,
    [headerContainerMispotlightCss]: mispotlight
  }, props.className)} />;
});
const searchInputContainerIsTabletCss = "s139l2lo";
const searchInputContainerCss = "s1ff6x6d";
const SearchInputContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  const isTablet = useDeviceType('isTablet');
  return <Component ref={ref} {...restProps} className={classNames(searchInputContainerCss, {
    [searchInputContainerIsTabletCss]: isTablet
  }, props.className)} />;
});
const rowIsPhoneCss = "rmtqggu";
const rowCss = "r10lcrrd";
const mobileScopeRowCss = "m1vywd35";
const mobileActionsRowCss = "mr4sdwu";

/** New-shell phone: inset search/filter row; edge-to-edge bleed has no horizontal padding. */
const mobileActionsRowEdgeToEdgeCss = "mu2bgu5";
const mobileSearchInputCss = "m1kji16w";
const mobileFilterControlCss = "m150xp99";
const schedulingSwitchRowCss = "sirsjs5";
const ctaSectionIsTabletCss = "cabhuo4";
const rowIsTabletCss = "r1zanl7";
const Row = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  const isPhone = useDeviceType('isPhone');
  const isTablet = useDeviceType('isTablet');
  return <Component ref={ref} {...restProps} className={classNames(rowCss, {
    [rowIsPhoneCss]: isPhone,
    [rowIsTabletCss]: isTablet
  }, props.className)} />;
});
const filterSectionCss = "f3cc9ti";
const FilterSection = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(filterSectionCss, props.className)} />;
});
const ctaSectionCss = "cw9drk6";
const CtaSection = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    keepInlineOnTablet = false,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  const isTablet = useDeviceType('isTablet');
  return <Component ref={ref} {...restProps} className={classNames(ctaSectionCss, {
    // The legacy tablet header lets the CTA section wrap to its own full-width row. In preset
    // mode the only CTA is the compact scheduling toggle, so keep it inline on the right.
    [ctaSectionIsTabletCss]: isTablet && !keepInlineOnTablet
  }, props.className)} />;
});
const MobileHeader = forwardRef(({
  scope,
  canExport,
  useResultsCount,
  isNotetakerEntitled,
  notetakerEnabled,
  filterDefaults,
  initialValue,
  onboardingTourSettingsRef,
  displayedMeetingCount = 0
}, ref) => {
  const location = useLocation();
  const showStepTwo = handleCheckStepEnabled(location, MI_MEETINGS_TOUR.STEP_TWO);
  const isDefaultPresetToggleEnabled = useFlag('sa_default_preset_toggle', false);
  const isPhone = useDeviceType('isPhone');
  const isProductRefreshAppShell = useProductRefreshAppShell();
  const useMobilePresetLayout = isDefaultPresetToggleEnabled && isPhone;
  return <HeaderContainer ref={ref} mispotlight={showStepTwo}>
      {!useMobilePresetLayout && <Row>
          <div className={mobileScopeRowCss} data-testid="meetings-mobile-scope-row">
            <ScopeDropdown fitContent />
          </div>
        </Row>}
      <Row className={classNames(mobileActionsRowCss, {
      // Only when PageFilterRenderer edgeToEdge removes ContentArea inset.
      [mobileActionsRowEdgeToEdgeCss]: isProductRefreshAppShell && isPhone
    })}>
        <div className={mobileSearchInputCss} data-testid="meetings-mobile-search-field">
          <SearchInput initialValue={initialValue} filterDefaults={filterDefaults} />
        </div>
        <div className={mobileFilterControlCss}>
          <MeetingsFilter useResultsCount={useResultsCount} filterDefaults={filterDefaults} notetakerEnabled={notetakerEnabled} />
        </div>
        {!isDefaultPresetToggleEnabled && canExport && <ExportButton text={null} scope={scope} filterDefaults={filterDefaults} />}
        {!isDefaultPresetToggleEnabled && <PersistentFiltersMenu ref={onboardingTourSettingsRef} notetakerEnabled={notetakerEnabled} />}
      </Row>
      <MeetingsPresetTabs displayedMeetingCount={displayedMeetingCount} stackedLayout edgeToEdgeInset={isProductRefreshAppShell && isPhone} />
      <StepTwo />
    </HeaderContainer>;
});
const DesktopHeader = forwardRef(({
  scope,
  selectedView,
  canExport,
  useResultsCount,
  clearActiveMeeting,
  notetakerEnabled,
  isNotetakerEntitled,
  filterDefaults,
  initialValue,
  onboardingTourSettingsRef,
  displayedMeetingCount = 0
}, ref) => {
  const location = useLocation();
  const showStepTwo = handleCheckStepEnabled(location, MI_MEETINGS_TOUR.STEP_TWO);
  const isDefaultPresetToggleEnabled = useFlag('sa_default_preset_toggle', false);
  return <HeaderContainer ref={ref} mispotlight={showStepTwo} data-pendo-id={!isNotetakerEntitled ? 'new-meetings-page' : undefined}>
      <Row>
        <FilterSection>
          <ScopeDropdown />
          <SearchInputContainer>
            <SearchInput initialValue={initialValue} filterDefaults={filterDefaults} />
          </SearchInputContainer>
          <MeetingsFilter useResultsCount={useResultsCount} filterDefaults={filterDefaults} notetakerEnabled={notetakerEnabled} />
        </FilterSection>
        <CtaSection keepInlineOnTablet={isDefaultPresetToggleEnabled}>
          {!isDefaultPresetToggleEnabled && canExport && <ExportButton text={I18n.t('publisher.meetings.export')} scope={scope} filterDefaults={filterDefaults} />}
          {!isDefaultPresetToggleEnabled && <PersistentFiltersMenu ref={onboardingTourSettingsRef} notetakerEnabled={notetakerEnabled} />}
          {isDefaultPresetToggleEnabled && <div className={schedulingSwitchRowCss}>
              <MeetingsSchedulingSourceSwitch />
            </div>}
        </CtaSection>
      </Row>
      <MeetingsPresetTabs displayedMeetingCount={displayedMeetingCount} />
      <StepTwo />
    </HeaderContainer>;
});
export const SearchFilterExport = forwardRef((props, ref) => {
  const dispatch = useDispatch();
  const isExportConfirmationVisible = useSelector(isExportConfirmationVisibleSelector);
  const isPhone = useDeviceType('isPhone');
  const isTablet = useDeviceType('isTablet');
  const isProductRefreshAppShell = useProductRefreshAppShell();
  const useResultsCount = createUseResultsCount(useGetMeetingsFilterCountQuery, useSearchMeetingsSupergraphFilterCountQuery);
  const {
    data: meetingIntelligenceUserData
  } = useGetMeetingIntelligenceUserSettingsQuery();
  const {
    isEntitled: isNotetakerEntitled
  } = useNotetakerEntitled();
  const notetakerAvailable = !!meetingIntelligenceUserData?.miEnabled || isNotetakerEntitled;
  const {
    getFilterParams
  } = useFilters();
  const filterParams = getFilterParams();
  const isDefaultPresetToggleEnabled = useFlag('sa_default_preset_toggle', false);
  const commonProps = {
    ref,
    useResultsCount,
    notetakerEnabled: notetakerAvailable,
    isNotetakerEntitled,
    initialValue: filterParams.search_keyword,
    displayedMeetingCount: props.displayedMeetingCount ?? 0,
    ...props
  };

  // In preset mode, tablet uses the desktop single-row layout (scope + search + filter together,
  // scheduling toggle on the right) per design. Phone keeps the stacked mobile layout, and
  // non-preset tablet keeps the existing compact mobile layout.
  const useMobileLayout = isPhone || isTablet && !isDefaultPresetToggleEnabled;
  const header = useMobileLayout ? <MobileHeader {...commonProps} /> : <DesktopHeader {...commonProps} />;
  const exportConfirmation = isExportConfirmationVisible && <ExportConfirmation onConfirm={() => dispatch(dismissExportConfirmation())} message={I18n.t('publisher.scheduled_events.export.excessive_data')} />;
  if (isProductRefreshAppShell) {
    return <>
        {/* Phone: sticky chrome includes Displaying N (Figma 16/12); list scrolls under it. */}
        <PageFilterRenderer edgeToEdge={isPhone}>{header}</PageFilterRenderer>
        {exportConfirmation}
      </>;
  }
  return <>
      {header}
      {exportConfirmation}
    </>;
});

require("./search_filter_export.wyw-in-js.css!=!../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./search_filter_export.js");